import { Box, Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { useGetMaterialRequirements } from "../hooks/useGetMaterialRequirements"

import { CompanyPreferenceSection } from "./CompanyPreferenceSection"
import { EditMaterialRequirementsDialog } from "./material-requirements/EditMaterialRequirementsDialog"
import { getLabelConfigurationDisplay, getShelfLifeDisplay } from "./material-requirements/utils"
import { PreferenceTwoColumnRow } from "./PreferenceTwoColumnRow"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface MaterialRequirementsProps {
  locationId: string
}

export const MaterialRequirements: FC<MaterialRequirementsProps> = ({ locationId }) => {
  const { preference, isFetching, refetch } = useGetMaterialRequirements(locationId)
  const { hasPermissions } = usePermissionManager()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const handleOpenDialog = () => {
    setIsDialogOpen(true)
  }

  const handleCloseDialog = (shouldRefetch?: boolean) => {
    setIsDialogOpen(false)
    if (shouldRefetch) {
      refetch()
    }
  }

  if (!preference) {
    return (
      <>
        <Fade in unmountOnExit timeout={500}>
          <InformationMissingAlert
            title={canEdit ? "Start by selecting material requirements" : "No configuration details have been added"}
            action={
              canEdit && (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={handleOpenDialog}
                  data-testid='material-requirements-add-button'
                >
                  Add Material Requirements
                </Button>
              )
            }
          />
        </Fade>
        {isDialogOpen && (
          <EditMaterialRequirementsDialog onClose={handleCloseDialog} locationId={locationId} initialData={null} />
        )}
      </>
    )
  }

  return (
    <>
      <CompanyPreferenceSection
        title='Material Requirements'
        onEdit={handleOpenDialog}
        canEdit={canEdit}
        editButtonTestId='material-requirements-edit-button'
        isLoading={isFetching}
      >
        <Box className='flex flex-col'>
          <PreferenceTwoColumnRow
            leftContent='Allows Recertified Material'
            rightContent={preference?.allowsRecertifiedMaterial ? "Yes" : "No"}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow
            leftContent='Prefers Newest Material'
            rightContent={preference?.prefersNewestMaterial ? "Yes" : "No"}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow
            leftContent='Shelf Life Requirement'
            rightContent={getShelfLifeDisplay(preference)}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow
            leftContent='Change Approval Required'
            rightContent={preference?.changeApprovalRequired ? "Yes" : "No"}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow
            leftContent='Prefers Fewest Lots'
            rightContent={preference?.prefersFewestLots ? "Yes" : "No"}
            shouldShowDivider
          />
          <PreferenceTwoColumnRow
            leftContent='Shipping Label'
            rightContent={getLabelConfigurationDisplay(preference?.labelConfiguration)}
          />
        </Box>
      </CompanyPreferenceSection>
      {isDialogOpen && (
        <EditMaterialRequirementsDialog onClose={handleCloseDialog} locationId={locationId} initialData={preference} />
      )}
    </>
  )
}
